// How a block trade snapshot figure is rendered, wherever it is rendered.
//
// Display units follow the desk's own template: share counts in lakhs, money in crores.
// Figures are rendered without a decimal tail, except where dropping it would destroy the
// number: CMP is the reference price the block is priced against, so the paise matter; a
// half-lakh block is not one lakh; a block absorbing 0.4 days of ADV is not zero days; the
// two figures the offer is judged on — the discount to CMP and the annual implied return —
// are decided in basis points, where 1% and 1.4% are different trades; and the position
// percentages the block is sized against (current holding, target, combined post-block) are
// read against each other, so 2.4% and 2.6% are different decisions.
//
// The stored snapshot is always plain shares and plain rupees; only display scales, so a
// formatter change here can never move a number the backend computed. Shared by the detail
// page and the chat message so a block cannot read one way on screen and another in chat.

import {
  formatCurrency,
  formatCurrencyInCrores,
  formatNumber,
  formatPercentage,
  formatQuantityInLakhs,
} from '@/utils/formatters';
import { BlockTradeMetric } from '@/types/blockTrade';

export type MetricFormatter = (value: number) => string;

export const asQty: MetricFormatter = value => formatQuantityInLakhs(value, 2);
export const asMoney: MetricFormatter = value => formatCurrencyInCrores(value, undefined, false, 2);
export const asPrice: MetricFormatter = value => formatCurrency(value, undefined, 0);
export const asCmp: MetricFormatter = value => formatCurrency(value, undefined, 2);
export const asPct: MetricFormatter = value => formatPercentage(value, 0);
export const asPrecisePct: MetricFormatter = value => formatPercentage(value, 2);
export const asDays: MetricFormatter = value => `${formatNumber(value, 2)} days`;

/** What a figure the system could not source renders as. Never a zero — a blank means
 *  "unknown", and the difference decides whether a desk trades on it. */
export const UNAVAILABLE = '—';

/** Render one snapshot figure. A null value is blank, never zero. */
export const renderMetric = (
  metric: BlockTradeMetric | undefined,
  format: MetricFormatter,
): string => (metric?.value == null ? UNAVAILABLE : format(metric.value));